import { useState, useRef, useEffect } from "react";
import "./MusicPlayer.css";

const MusicPlayer = ({ musicPlaying, setMusicPlaying }) => {
  const audioRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (audioRef.current) {
      if (musicPlaying) {
        audioRef.current.play().catch((error) => {
          console.log("Autoplay prevented. User interaction required:", error);
          setError("Click the button to play music");
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [musicPlaying]);

  // ============================================
  // MUSIC SOURCE - CHOOSE ONE OPTION:
  // ============================================
  
  // ✅ OPTION 1: Local file in public folder (BEST - No CORS issues!)
  // 1. Put your music file (MP3) in the 'public' folder
  // 2. Name it something like 'music.mp3' or 'romantic-song.mp3'
  // 3. Use the path like this:
  const musicSrc = "/Olivia Dean - So Easy (To Fall In Love).mp3"; // Change 'music.mp3' to your file name
  
  // ❌ OPTION 2: Google Drive (CORS blocked - won't work directly)
  // Google Drive blocks audio playback due to CORS policy
  // const fileId = "19x6iHu0CRw4HVIIgOxX0EkNeJLK0apwe";
  // const musicSrc = `https://drive.google.com/uc?export=download&id=${fileId}`;
  
  // ✅ OPTION 3: Dropbox (Works better than Google Drive)
  // 1. Upload to Dropbox
  // 2. Get share link and change ?dl=0 to ?dl=1
  // Example: const musicSrc = "https://www.dropbox.com/s/xxxxx/song.mp3?dl=1";
  
  // ✅ OPTION 4: CORS Proxy (Workaround for Google Drive - not recommended for production)
  // const fileId = "19x6iHu0CRw4HVIIgOxX0EkNeJLK0apwe";
  // const musicSrc = `https://corsproxy.io/?https://drive.google.com/uc?export=download&id=${fileId}`;

  const handleToggle = () => {
    setError(null);
    setMusicPlaying(!musicPlaying);
  };

  const handleError = (e) => {
    console.error("Audio error:", e);
    setError("Music file not found. Put your MP3 file in the 'public' folder.");
    
    const audio = e.target;
    if (audio.error) {
      switch(audio.error.code) {
        case audio.error.MEDIA_ERR_ABORTED:
          console.log("Audio loading aborted");
          break;
        case audio.error.MEDIA_ERR_NETWORK:
          console.log("Network error");
          setError("Network error. Check your connection.");
          break;
        case audio.error.MEDIA_ERR_DECODE:
          console.log("Audio decode error");
          setError("Audio format not supported. Use MP3 format.");
          break;
        case audio.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          console.log("Audio source not supported");
          setError("File not found. Check the file name in public folder.");
          break;
        default:
          console.log("Unknown audio error");
      }
    }
  };

  return (
    <div className="music-player">
      <button
        className="music-toggle"
        onClick={handleToggle}
        aria-label={musicPlaying ? "Pause music" : "Play music"}
        onTouchStart={(e) => {
          e.preventDefault();
          handleToggle();
        }}
      >
        <span className="music-icon">{musicPlaying ? "🎵" : "🔇"}</span>
        {musicPlaying && <span className="music-wave">♪</span>}
      </button>

      {/* Error message */}
      {error && (
        <div className="music-error">
          <small>{error}</small>
        </div>
      )}

      {/* Audio Player */}
      <audio
        ref={audioRef}
        src={musicSrc}
        loop
        preload="metadata"
        onError={handleError}
        onLoadStart={() => {
          console.log("Loading audio from:", musicSrc);
        }}
        onCanPlay={() => {
          console.log("Audio ready to play");
          setError(null);
        }}
      />

      {/* Help text */}
      <div className="music-help">
        <small>💡 To fix CORS issue:</small>
        <small>1. Download your Google Drive file</small>
        <small>2. Put it in the 'public' folder</small>
        <small>3. Update musicSrc to "/your-file.mp3"</small>
      </div>
    </div>
  );
};

export default MusicPlayer;
